import TransitionAccept from "./TransitionAccept"
import { getScheduleForSelection } from "./scheduleSelection"
import {
	convertMinutesTo12HHMM,
	convertMinutesToHoursAndMinutesString
} from "../../../utils/generalUtils"
import React, { useState } from "react"
import { calculateTotalAcuity } from "../../../utils/realTimeNurseUtils"
import useLogging from "../../../hooks/useLogging"

const validateReassignment = (reassignment, patientsToUse, nursesToUse, chairsToUse, logging) => {
	if (
		reassignment.patientId &&
		!patientsToUse.find(patient => patient.patientId === reassignment.patientId)
	) {
		logging.error(`Unknown patient (${reassignment.patientId})`)
	}

	if (
		reassignment.newNurseId &&
		!nursesToUse.find(nurse => nurse.nurseId === reassignment.newNurseId)
	) {
		logging.error(`Unknown nurse (${reassignment.newNurseId})`)
	}

	if (
		reassignment.oldNurseId &&
		!nursesToUse.find(nurse => nurse.nurseId === reassignment.oldNurseId)
	) {
		logging.error(`Unknown nurse (${reassignment.oldNurseId})`)
	}

	if (reassignment.oldNurseId && !reassignment.newNurseId) {
		logging.error(`New nurse is required if you have the old nurse id`)
	}

	if (
		reassignment.oldChairId &&
		!chairsToUse.find(chair => chair.chairId === reassignment.oldChairId)
	) {
		logging.error(`Unknown chair (${reassignment.oldChairId})`)
	}

	if (
		reassignment.newChairId &&
		!chairsToUse.find(chair => chair.chairId === reassignment.newChairId)
	) {
		logging.error(`Unknown chair (${reassignment.newChairId})`)
	}

	if (reassignment.oldChairId && !reassignment.newChairId) {
		logging.error(`New chair is required if you have the old chair id`)
	}
}

const totalNurseChangeInAcuity = (patientsToUse, nursesToUse, reassignments) => {
	let nurseChanges = {}

	for (const nurse of nursesToUse) {
		nurseChanges[nurse.nurseId] = 0
	}

	for (const reassignment of reassignments) {
		const patient = patientsToUse.find(patient => patient.patientId === reassignment.patientId)
		if (!patient) {
			throw new Error(`Cannot find patient with id ${reassignment.patientId}`)
		}

		const oldNurseId = patient.assignedNurse
		const newNurseId = reassignment.newNurseId || oldNurseId
		const nurseChanging = oldNurseId !== newNurseId

		if (nurseChanging) {
			nurseChanges[oldNurseId] -= patient.acuity
			nurseChanges[newNurseId] += patient.acuity
		}
	}

	return nurseChanges
}

const convertReassignmentToDetailedMessage = (
	reassignment,
	patientsToUse,
	nursesToUse,
	chairsToUse,
	nurseChanges,
	verbose,
	logging
) => {
	validateReassignment(reassignment, patientsToUse, nursesToUse, chairsToUse, logging)

	const patient = patientsToUse.find(patient => patient.patientId === reassignment.patientId)
	if (!patient) {
		logging.error(`Cannot find patient with id ${reassignment.patientId}`)
		throw new Error(`Cannot find patient with id ${reassignment.patientId}`)
	}
	const oldNurseId = patient.assignedNurse
	const newNurseId = reassignment.newNurseId || oldNurseId
	const nurseChanging = oldNurseId !== newNurseId

	const oldChairId = patient.assignedChair
	const newChairId = reassignment.newChairId || oldChairId
	const chairChanging = oldChairId !== newChairId

	if (!nurseChanging && !chairChanging) {
		return
	}

	let assignOrReassign = "Assign"
	if (oldNurseId || oldChairId) {
		assignOrReassign = "Reassign"
	}

	let message = `${assignOrReassign} **${patient.patientName || `Unknown (${patient.patientId})`}** ${patient.infusionType ? `· ${patient.infusionType}` : ""}`

	if (verbose) {
		message += ` ${patient.acuity ? `· Acuity: ${patient.acuity}` : ""} ${patient.scheduledStartTime ? `· Start: ${convertMinutesTo12HHMM(patient.scheduledStartTime)}` : ""} ${patient.length ? `· Duration: ${convertMinutesToHoursAndMinutesString(patient.length)}` : ""}`
	}

	if (oldNurseId && nurseChanging) {
		const oldNurse = nursesToUse.find(nurse => nurse.nurseId === oldNurseId)
		if (!oldNurse) {
			logging.error(`Cannot find nurse with id ${oldNurseId}`)
			throw new Error(`Cannot find nurse with id ${oldNurseId}`)
		}
		message += ` from **${oldNurse.nurseName}**`

		const oldNurseAcuity = oldNurse
			? calculateTotalAcuity(oldNurse, patientsToUse)
			: "Unknown Acuity"
		message += ` (Total Acuity: ${oldNurseAcuity} ➔ ${oldNurseAcuity + nurseChanges[oldNurseId]})`
	}
	if (nurseChanging) {
		const newNurse = nursesToUse.find(nurse => nurse.nurseId === newNurseId)
		if (!newNurse) {
			logging.error(`Cannot find nurse with id ${newNurseId}`)
			throw new Error(`Cannot find nurse with id ${newNurseId}`)
		}
		message += ` to **${newNurse.nurseName}**`
		const newNurseAcuity = newNurse
			? calculateTotalAcuity(newNurse, patientsToUse)
			: "Unknown Acuity"
		message += ` (Total Acuity: ${newNurseAcuity} ➔ ${newNurseAcuity + nurseChanges[newNurseId]})`
	}
	if (chairChanging && nurseChanging) {
		message += ` and `
	}
	if (oldChairId && chairChanging) {
		const oldChair = chairsToUse.find(chair => chair.chairId === oldChairId)
		if (!oldChair) {
			throw new Error(`Cannot find chair with id ${oldChairId}`)
		}
		message += ` from Chair **${oldChair.chairName}**`
	}
	if (newChairId !== oldChairId) {
		const newChair = chairsToUse.find(chair => chair.chairId === newChairId)
		if (!newChair) {
			throw new Error(`Cannot find chair with id ${newChairId}`)
		}
		message += ` to Chair **${newChair.chairName}**`
	}

	if (
		(reassignment.oldNurseId && reassignment.newNurseId) ||
		(reassignment.oldChairId && reassignment.newChairId)
	) {
		message = message.replace("Assign", "Reassign")
	}

	return message
}

const PatientReassignmentConfirmation = ({
	title,
	message,
	isOpen,
	onAccept,
	onDecline,
	remove,
	reassignments,
	scheduleSelection
}) => {
	const [isVerbose, setIsVerbose] = useState(false)
	const currentState = getScheduleForSelection(scheduleSelection)
	const logging = useLogging()

	const nurseChanges = totalNurseChangeInAcuity(
		currentState.patientSchedule,
		currentState.nurseSchedule,
		reassignments
	)

	const reassignmentStrings = reassignments.map(reassignment =>
		isVerbose
			? convertReassignmentToDetailedMessage(
					reassignment,
					currentState.patientSchedule,
					currentState.nurseSchedule,
					currentState.chairSchedule,
					nurseChanges,
					true,
					logging
				)
			: convertReassignmentToDetailedMessage(
					reassignment,

					currentState.patientSchedule,
					currentState.nurseSchedule,
					currentState.chairSchedule,
					nurseChanges,
					false,
					logging
				)
	)

	if (!isOpen) {
		return null
	}

	return (
		<div>
			<TransitionAccept
				title={title}
				message={`${message}\n\n${reassignmentStrings.map(detail => `- ${detail}`).join("\n")}`}
				isOpen={isOpen}
				onAccept={onAccept}
				onDecline={onDecline}
				remove={remove}
				verbose={isVerbose}
				setVerbose={setIsVerbose}
			/>
		</div>
	)
}

export default PatientReassignmentConfirmation
